import { useState, useEffect, useRef } from "react";
import { validateKey, isUnlocked } from "../modules/licenseGate";
import "./LicenseGate.css";

/*
  LicenseGate.jsx — License key entry and validation UI

  Shows a prompt to enter a license key to unlock paid features.
  Once validated, disappears and the paid features render in its place.

  If the user arrives via ?license=1 (from "I have a license" on the
  landing page), the input is auto-focused so they can type immediately.
*/

export default function LicenseGate({ onUnlock }) {
  const [key, setKey] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const inputRef = useRef(null);
  const unlockTimerRef = useRef(null);

  // Clear any pending unlock timer on unmount
  useEffect(() => {
    return () => {
      if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
    };
  }, []);

  // If already unlocked this session, notify the parent after mount
  useEffect(() => {
    if (isUnlocked()) {
      onUnlock();
      return;
    }
    // Auto-focus the input if the user came from "I have a license"
    const params = new URLSearchParams(window.location.search);
    if (params.get("license") === "1" && inputRef.current) {
      inputRef.current.focus();
      // Scroll the license gate into view smoothly
      inputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [onUnlock]);

  async function handleValidate() {
    if (!key.trim()) return;
    setLoading(true);
    setMessage("");
    setError(false);

    const result = await validateKey(key);

    setLoading(false);
    setMessage(result.message);
    setError(!result.valid);

    if (result.valid) {
      // Small delay so the user sees the success message before UI changes
      unlockTimerRef.current = setTimeout(() => onUnlock(), 800);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") handleValidate();
  }

  return (
    <div className="license-gate">
      <div className="license-gate-inner">
        <p className="license-gate-title">Already have a Pro license?</p>
        <p className="license-gate-desc">
          Enter your key to unlock multi-file upload, date range filtering, CSV and KML export.{" "}
          <a href="https://aissift.lemonsqueezy.com" target="_blank" rel="noreferrer">
            Get a license →
          </a>
        </p>
        <div className="license-gate-row">
          <input
            ref={inputRef}
            className="license-gate-input"
            type="text"
            placeholder="AISS-XXXX-XXXX-XXXX"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          <button
            className="license-gate-button"
            onClick={handleValidate}
            disabled={loading || !key.trim()}
          >
            {loading ? "Checking..." : "Unlock"}
          </button>
        </div>
        {message && (
          <p className={`license-gate-message ${error ? "license-gate-message--error" : "license-gate-message--success"}`}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
}
